
import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Home, BookOpen, CheckSquare, FolderOpen, Trophy, Settings, LogOut } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { supabase } from '@/integrations/supabase/client'
import { useNavigate } from 'react-router-dom'
import { useProfileRealtime } from '@/hooks/useProfileRealtime'
import { useLevelCalculation } from '@/hooks/useLevelCalculation'

interface UserProfile {
  first_name: string | null
  last_name: string | null
  level: number
  points: number
}

const Sidebar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const { calculateLevel, getCurrentLevelProgress, getPointsToNextLevel } = useLevelCalculation()

  const fetchUserProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, level, points')
        .eq('id', session.user.id)
        .single()

      if (profile) {
        setUserProfile(profile)
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
    }
  }

  useEffect(() => {
    fetchUserProfile()
  }, [])

  // Set up real-time updates for profile changes (including points updates)
  useProfileRealtime({
    onProfileChange: fetchUserProfile
  })

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  const isActive = (path: string) => location.pathname === path

  const menuItems = [
    { icon: Home, label: 'Dashboard', path: '/dashboard' },
    { icon: BookOpen, label: 'Learn', path: '/learn' },
    { icon: CheckSquare, label: 'Tasks', path: '/tasks' },
    { icon: FolderOpen, label: 'Resources', path: '/resources' },
    { icon: Trophy, label: 'Rewards', path: '/rewards' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ]

  // Calculate level and progress based on points
  const actualLevel = userProfile ? calculateLevel(userProfile.points) : 1
  const currentLevelProgress = userProfile ? getCurrentLevelProgress(userProfile.points) : 0
  const pointsToNext = userProfile ? getPointsToNextLevel(userProfile.points) : 500
  const progressPercentage = (currentLevelProgress / 500) * 100

  return (
    <div className="w-64 bg-white border-r border-gray-200 h-screen flex flex-col">
      {/* User Profile Section */}
      {userProfile && (
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="text-lg font-semibold text-gray-900 mb-2">
            {userProfile.first_name} {userProfile.last_name}
          </div>
          <div className="text-sm text-gray-600 mb-3">
            Level {actualLevel} • {userProfile.points} points
          </div>
          <div className="space-y-2">
            <Progress value={progressPercentage} className="h-2" />
            <div className="text-xs text-gray-500">
              {actualLevel >= 100 ? 'Max Level Reached!' : `${pointsToNext} points to level ${actualLevel + 1}`}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {menuItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
              isActive(item.path)
                ? 'bg-purple-100 text-purple-600'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </Link>
        ))}
      </nav>

      {/* Logout - Always at bottom */}
      <div className="p-4 border-t border-gray-200 flex-shrink-0">
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-3 py-2 text-gray-600 hover:bg-gray-100"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </Button>
      </div>
    </div>
  )
}

export default Sidebar
